// Shared narrative adapter for every public /modules/:key page.
//
// This file does not introduce a second marketing-content catalogue. It turns the existing,
// reviewed moduleCatalog + moduleProfiles records into one consistent buyer evaluation map:
// school job -> required context -> supported product work -> reviewable result. These are three
// non-sequential evaluation lenses. The detailed language remains the unique authored
// FEATURE_CONTENT/profile copy for the route.

const ensureSentence = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

// `FEATURE_CONTENT.ai` is intentionally only a catalogue flag. It does not say whether AI is the
// capability itself, an optional helper inside an otherwise deterministic workflow, or merely the
// explanation layer over platform-computed results. Keep that buyer-critical distinction explicit.
//
// Indexes point back to the reviewed `detail.bullets` and profile prerequisites. No AI promise is
// generated from a module name, badge or keyword match, and no duplicate marketing copy lives here.
const AI_SUPPORT_PRESENTATION = Object.freeze({
  "lesson-plan-management": Object.freeze({ mode: "optional", capabilityIndexes: [2, 3], prerequisiteIndexes: [1] }),
  "homework-and-assignments": Object.freeze({ mode: "optional", capabilityIndexes: [2], prerequisiteIndexes: [] }),
  "exam-scheduling": Object.freeze({ mode: "optional", capabilityIndexes: [3], prerequisiteIndexes: [] }),
  "result-publishing": Object.freeze({ mode: "optional", capabilityIndexes: [2], prerequisiteIndexes: [] }),
  "student-performance": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1, 3], prerequisiteIndexes: [1] }),
  "enquiry-management": Object.freeze({ mode: "optional", capabilityIndexes: [2], prerequisiteIndexes: [1] }),
  // Task Management has reviewed optional Aira proof evaluation even though its legacy catalogue
  // boolean is false. Listing it here prevents a real, conditional capability from disappearing.
  "task-management": Object.freeze({ mode: "optional", capabilityIndexes: [2], prerequisiteIndexes: [1] }),
  "expense-management": Object.freeze({ mode: "hybrid", capabilityIndexes: [1], prerequisiteIndexes: [1] }),
  "fee-reminder": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "financial-summary": Object.freeze({ mode: "hybrid", capabilityIndexes: [1, 2], prerequisiteIndexes: [1] }),
  "exam-reports": Object.freeze({ mode: "hybrid", capabilityIndexes: [2, 3], prerequisiteIndexes: [1] }),
  "dashboard-analytics": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1], prerequisiteIndexes: [1] }),
  // The score and driver contributions are deterministic. The page must not recast them as a
  // model decision merely because the feature sits behind the Insights entitlement.
  "performance-insights": Object.freeze({ mode: "deterministic", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "daily-summary": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1, 3], prerequisiteIndexes: [1] }),
  "mis-reports": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1, 2], prerequisiteIndexes: [1] }),
  "parent-app": Object.freeze({ mode: "optional", capabilityIndexes: [2], prerequisiteIndexes: [] }),
  "teacher-app": Object.freeze({ mode: "optional", capabilityIndexes: [3], prerequisiteIndexes: [1] }),
  "automated-fee-reminders": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 2, 3], prerequisiteIndexes: [0] }),
  "circular-management": Object.freeze({ mode: "optional", capabilityIndexes: [0], prerequisiteIndexes: [1] }),
  "aira-assistant": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0, 1] }),
  "parent-aira": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "voice-aira": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "migration-copilot": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  // Compliance uses deterministic rules for findings and Aira only for an optional explanation.
  "compliance-ai": Object.freeze({ mode: "hybrid", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0, 1] }),
  "report-card-ai": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "website-builder-ai": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
  "talk-to-your-data": Object.freeze({ mode: "dedicated", capabilityIndexes: [0, 1, 2, 3], prerequisiteIndexes: [0] }),
});

const AI_SUPPORT_LABELS = Object.freeze({
  dedicated: "Dedicated Aira capability",
  optional: "Optional Aira or AI assistance",
  hybrid: "Deterministic workflow with governed AI assistance",
  deterministic: "Deterministic insight — not a model decision",
});

function aiSupportFor(mod, capabilities, prerequisites) {
  const presentation = AI_SUPPORT_PRESENTATION[mod?.key];
  if (!presentation) return null;
  const points = presentation.capabilityIndexes.map((index) => capabilities[index]).filter(Boolean);
  const requirements = presentation.prerequisiteIndexes.map((index) => prerequisites[index]).filter(Boolean);
  return Object.freeze({
    mode: presentation.mode,
    label: AI_SUPPORT_LABELS[presentation.mode],
    points: Object.freeze(points),
    requirements: Object.freeze(requirements),
  });
}

export function moduleProblemFor(mod) {
  const name = mod?.name || "this capability";
  const job = ensureSentence(mod?.oneLiner || mod?.detail?.what || "Keep the relevant school work in one authorised context");
  return `School teams need a dependable way to handle ${name}: ${job} The required records, assigned roles and review boundary must remain explicit.`;
}

export function moduleNarrativeFor(mod, profile) {
  const capabilities = Object.freeze((mod?.detail?.bullets || []).filter(Boolean));
  const roles = Object.freeze((profile?.roles || []).filter(Boolean));
  const prerequisites = Object.freeze((profile?.prerequisites || []).filter(Boolean));
  const connectedWorkflowKeys = Object.freeze((profile?.related || []).filter(Boolean));
  const context = prerequisites[0]
    ? `${prerequisites[0]} must be confirmed before rollout`
    : `Current ${mod?.workspaceTitle || mod?.groupTitle || "school"} records and the permissions assigned to ${roles.slice(0, 2).join(" and ") || "authorised staff"}`;
  const work = capabilities[0] || mod?.oneLiner;
  const output = profile?.outcome || mod?.oneLiner;
  const whatItDoes = ensureSentence(mod?.detail?.what || mod?.oneLiner);
  const schoolGets = ensureSentence(output);

  return Object.freeze({
    problem: moduleProblemFor(mod),
    steps: Object.freeze([
      Object.freeze({ id: "context", eyebrow: "Evaluation lens", title: "Required context", detail: ensureSentence(context) }),
      Object.freeze({ id: "work", eyebrow: "Evaluation lens", title: "Supported work", detail: ensureSentence(work) }),
      Object.freeze({ id: "output", eyebrow: "Evaluation lens", title: "Reviewable result", detail: schoolGets }),
    ]),
    capabilities,
    // Buyer-facing content model. Every value below is a direct view over reviewed catalogue/profile
    // data so ModulePage can change its hierarchy without creating a second source of product truth.
    whatItDoes,
    helpsManage: Object.freeze({
      summary: ensureSentence(mod?.oneLiner || whatItDoes),
      points: capabilities,
    }),
    schoolGets,
    users: roles,
    connectedWorkflowKeys,
    aiSupport: aiSupportFor(mod, capabilities, prerequisites),
    beforeRollout: Object.freeze({
      prerequisites,
      status: mod?.publicStatus || null,
    }),
  });
}
